import { createContext, useContext } from '@berlin-front/common/composables/context';
import type { ContextValueType } from '@berlin-front/common/composables/context';
import { reactive, computed, nextTick, ref, watch } from '@vue/composition-api';
import { useRouter } from '@berlin-front/common/composables/useRouter';
import { useRoute } from '@berlin-front/common/composables/useRoute';
import { ApiCacheContext } from '@/services/ApiCacheContext';
import { ROUTE_NAME } from '@/router/route-name';
import { formatNumber } from '@berlin-front/common/utils';
import { POINT_USE_TYPE, PointUseType } from '@/entity/pointUseType';
import { AMOUNT_DISPLAY_TYPE, AmountDisplayType } from '@/entity/amountDisplayType';

/**
 * 決済処理のcomposable
 *
 * 画面から使用するcomposableには非同期処理を書かない。
 * @returns
 */
export const useSettlement = () => {
  const router = useRouter();
  const route = useRoute();
  const { atoneShopV2OptionGetResult, atoneMethodGetResult, atonePointGetResult, atoneModalParameter, userFullName } =
    useContext(ApiCacheContext);
  const pointUseType = computed(
    () => (atoneMethodGetResult.value?.atonePayment?.pointUseType ?? POINT_USE_TYPE.DISABLED) as PointUseType,
  );
  const amountDisplayType = computed(
    () =>
      (atoneShopV2OptionGetResult.value?.atoneServiceOption.amountDisplayType ??
        AMOUNT_DISPLAY_TYPE.NORMAL) as AmountDisplayType,
  );
  const itemTotalPrice = computed(() => atoneModalParameter.value.payment.amount);
  const ownPoint = computed(() => atonePointGetResult.value?.point || 0);
  const maxUsablePoint = computed(() => Math.min(Math.max(0, ownPoint.value || 0), itemTotalPrice.value));

  // 状態
  const alert = ref([route.query.alert ?? []].flat().flatMap((v) => (v === null ? [] : [v])));
  const state = reactive({
    point: 0 as number | '',
  });
  const onInput = (value: string) => {
    if (value.length === 0) {
      state.point = '';
    } else if (/^\d{1,10}$/.test(value)) {
      state.point = Number.parseInt(value, 10);
    } else {
      state.point = (state.point || 0) + 1;
      void nextTick().then(() => {
        state.point = (state.point || 0) - 1;
      });
    }
  };
  const onBlur = () => {
    if (state.point === '' || state.point < 0) {
      state.point = 0;
    } else if (state.point > maxUsablePoint.value) {
      state.point = maxUsablePoint.value;
    }
  };
  watch(
    () => state.point,
    () => {
      if (alert.value.length > 0) {
        alert.value = [];
      }
    },
  );

  // 表示項目
  const shopName = computed(() => atoneShopV2OptionGetResult.value?.shopName ?? '');
  const retentionPoint = computed(() => atonePointGetResult.value?.point ?? 0);
  const subtractPoint = computed(() => {
    if (state.point === '' || Number.isNaN(state.point) || state.point < 0) {
      return '0';
    }
    return formatNumber(Math.min(state.point || 0, maxUsablePoint.value));
  });
  const totalAmount = computed(() => itemTotalPrice.value - Math.min(state.point || 0, maxUsablePoint.value));
  const hasPoint = computed(() => ownPoint.value > 0 && pointUseType.value === POINT_USE_TYPE.ENABLED);
  // ポイントシステムでエラーが発生してるとき（pointは負数。）
  const npcSystemHasError = computed(() => ownPoint.value < 0 && pointUseType.value === POINT_USE_TYPE.ENABLED);
  const memberName = userFullName;

  // そのままmutateをキックするのではなく、
  // - validation結果を確認
  // - 実行中でないことを確認
  // - 実行後（正常系）でないことを確認
  // した上でリクエストを行う。
  const onSubmit = () => {
    void router.replace({ name: ROUTE_NAME.ATONE_LOADING_SETTLEMENT, params: { point: state.point.toString() } });
  };

  return reactive({
    // 状態
    alert,
    state,
    pointUseType,
    amountDisplayType,
    // 表示項目
    shopName,
    retentionPoint,
    subtractPoint,
    itemTotalPrice,
    totalAmount,
    hasPoint,
    npcSystemHasError,
    memberName,
    onBlur,

    // action
    onSubmit,

    onInput,
  });
};

export const ConfirmSettlementContext = createContext({ useSettlement });

export type ConfirmSettlementContextType = ContextValueType<typeof ConfirmSettlementContext>;
